import type { ExpressAvailabilityStatus } from "@dransay/api";

export type ExpressCourierDisabledReason = "location" | "time" | "unavailable";

/**
 * Why the express-courier card is offered but not selectable.
 *
 * unknown (including an unreachable fulfillment service) keeps hiding the card
 * entirely (FR-007) — the platform only explains a reason it can actually
 * resolve (FR-059). A failed check disables the card even if it is the cart's
 * currently stored delivery method (FR-062) — a resolved
 * unavailable_location/unavailable_time means it genuinely cannot be fulfilled,
 * so it must never appear as a valid, selected option. A
 * settled-but-indeterminate outcome (status stays "unknown" once loading
 * finishes, e.g. the fulfillment service is unreachable) must disable it the
 * same way when it's the current selection — otherwise an unconfirmed method
 * would still look like a valid, selected option.
 */
export function resolveExpressCourierDisabledReason({
	status,
	isSelected,
	isChecking,
}: {
	status: ExpressAvailabilityStatus;
	isSelected: boolean;
	isChecking: boolean;
}): ExpressCourierDisabledReason | undefined {
	if (status === "unavailable_location") {
		return "location";
	}
	if (status === "unavailable_time") {
		return "time";
	}
	if (isSelected && !isChecking && status === "unknown") {
		return "unavailable";
	}
	return undefined;
}
